import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n, EVENT_TYPES } from "@/lib/i18n";
import { SectionHeading, GoldButton } from "@/components/Brand";
import { Facebook } from "lucide-react";
import QrInstall from "@/components/QrInstall";

const field = "min-h-[48px] w-full border border-[#C69A3B]/60 bg-white px-4 text-base";

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "", event_date: "", location: "", message: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", event_type: "", event_date: "", location: "", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-14 sm:px-8 lg:px-12 lg:py-20" data-testid="contact-page">
      <SectionHeading label="HR Creations" title={t("contact.title")} sub={t("contact.sub")} />
      <div className="mt-8 max-w-3xl border border-[#C69A3B] bg-[#F6F0E4] p-6" data-testid="contact-email-block">
        <p className="label-mono">{t("contact.emailUs")}</p>
        <a
          href="mailto:hrcreations90@gmail.com"
          data-testid="contact-public-email"
          className="mt-3 inline-block font-serif-display text-xl text-[#8F3329] underline sm:text-2xl"
        >
          hrcreations90@gmail.com
        </a>
        <p className="mt-3 text-sm text-[#5A5752]">{t("contact.emailNote")}</p>
        <a
          href="https://www.facebook.com/share/1SYWj1kpQz/?mibextid=wwXIfr"
          target="_blank"
          rel="noreferrer"
          data-testid="contact-facebook"
          className="mt-5 inline-flex min-h-[44px] items-center gap-2 border border-[#C69A3B] px-5 text-sm text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
        >
          <Facebook size={15} /> {t("contact.facebook")}
        </a>
      </div>
      <div className="mt-12 max-w-2xl">
        <QrInstall />
      </div>

      <form onSubmit={submit} className="mt-12 grid max-w-3xl gap-5 sm:grid-cols-2" data-testid="contact-form">
        <div className="sm:col-span-1">
          <label className="label-mono mb-2 block" htmlFor="c-name">{t("contact.name")}</label>
          <input id="c-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className={field} />
        </div>
        <div>
          <label className="label-mono mb-2 block" htmlFor="c-email">{t("contact.email")}</label>
          <input id="c-email" type="email" data-testid="contact-email" required value={form.email} onChange={set("email")} className={field} />
        </div>
        <div>
          <label className="label-mono mb-2 block" htmlFor="c-phone">{t("contact.phone")}</label>
          <input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={field} />
        </div>
        <div>
          <label className="label-mono mb-2 block" htmlFor="c-type">{t("contact.eventType")}</label>
          <select id="c-type" data-testid="contact-event-type" value={form.event_type} onChange={set("event_type")} className={field}>
            <option value="">—</option>
            {Object.keys(EVENT_TYPES).map((k) => (
              <option key={k} value={k}>{EVENT_TYPES[k][lang]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-mono mb-2 block" htmlFor="c-date">{t("contact.eventDate")}</label>
          <input id="c-date" type="date" data-testid="contact-event-date" value={form.event_date} onChange={set("event_date")} className={field} />
        </div>
        <div>
          <label className="label-mono mb-2 block" htmlFor="c-loc">{t("contact.location")}</label>
          <input id="c-loc" data-testid="contact-location" value={form.location} onChange={set("location")} className={field} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-mono mb-2 block" htmlFor="c-msg">{t("contact.message")}</label>
          <textarea id="c-msg" rows={6} data-testid="contact-message" required value={form.message} onChange={set("message")} className="w-full border border-[#C69A3B]/60 bg-white p-4 text-base" />
        </div>
        <div className="sm:col-span-2">
          <GoldButton type="submit" disabled={busy} data-testid="contact-submit">
            {busy ? t("common.loading") : t("contact.submit")}
          </GoldButton>
          {sent && (
            <p className="mt-4 text-sm text-[#275E64]" data-testid="contact-success">
              {t("contact.success")}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
